import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import Logo from '../components/Logo';
import Header from '../components/Header';
import BadgeDisplay from '../components/BadgeDisplay';
import { getHostAchievementInfo } from '../utils/hostAchievements';

const PublicHostProfile = () => {
  const { hostId } = useParams();
  const navigate = useNavigate();
  const [hostData, setHostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadHostData = async () => {
      if (!hostId) {
        setError('Host ID is required');
        setLoading(false);
        return;
      }

      try {
        const hostDoc = await getDoc(doc(db, 'users', hostId));
        
        if (!hostDoc.exists()) {
          setError('Host not found');
          setLoading(false);
          return;
        }

        const data = hostDoc.data();
        
        // Only show hosts (not other user types)
        if (data.role !== 'host') {
          setError('Profile not available');
          setLoading(false);
          return;
        }

        setHostData({ id: hostDoc.id, ...data });
      } catch (error) {
        console.error('Error loading host data:', error);
        setError('Failed to load host profile');
      } finally {
        setLoading(false);
      }
    };

    loadHostData();
  }, [hostId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-white">Loading host profile...</p>
        </div>
      </div>
    );
  }

  if (error || !hostData) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Logo className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Host Not Found</h1>
          <p className="text-gray-400 mb-6">The host profile you're looking for doesn't exist or is not available.</p>
          <button
            onClick={() => navigate('/player-hub')}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            Back to Player Hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <div className="max-w-4xl mx-auto p-6">
        {/* Profile Header */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-8">
          <div className="flex items-start gap-6">
            {/* Profile Image */}
            <div className="flex-shrink-0">
              {hostData.profileImageUrl ? (
                <img
                  src={hostData.profileImageUrl}
                  alt={hostData.fullName}
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-600"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-700 border-4 border-gray-600 flex items-center justify-center text-4xl text-gray-400">
                  👤
                </div>
              )}
            </div>
            
            {/* Profile Info */}
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white mb-2">
                {hostData.fullName || 'Host'}
              </h1>
              
              {/* Host Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-500">{hostData.gamesHosted || 0}</div>
                  <div className="text-gray-400 text-xs">Games Hosted</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-500">{hostData.averageRating?.toFixed(1) || 'N/A'}</div>
                  <div className="text-gray-400 text-xs">Avg Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-yellow-500">{hostData.achievements?.length || 0}</div>
                  <div className="text-gray-400 text-xs">Badges Earned</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-purple-500">{hostData.bookingInquiries || 0}</div>
                  <div className="text-gray-400 text-xs">Booking Inquiries</div>
                </div>
              </div>
              
              {/* Achievements */}
              <div className="mb-4">
                <BadgeDisplay 
                  achievements={hostData.achievements}
                  getAchievementInfo={getHostAchievementInfo}
                  title="Host Achievements"
                  emptyMessage="No achievements yet. Keep hosting to earn badges!"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bio */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Bio
              </label>
              <p className="text-gray-300 bg-gray-700 rounded px-3 py-2 min-h-[6rem]">
                {hostData.bio || 'No bio added yet.'}
              </p>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Location
              </label>
              <p className="text-gray-300 bg-gray-700 rounded px-3 py-2">
                {hostData.location || 'Not specified'}
              </p>
            </div>

            {/* Hosting Experience */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Hosting Experience
              </label>
              <p className="text-gray-300 bg-gray-700 rounded px-3 py-2">
                {hostData.hostingExperience || 'Not specified'}
              </p>
            </div>

            {/* Preferred Categories */}
            {hostData.preferredCategories && hostData.preferredCategories.length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Preferred Categories
                </label>
                <div className="flex flex-wrap gap-2">
                  {hostData.preferredCategories.map((category, index) => (
                    <span
                      key={index}
                      className="bg-green-600 text-white px-3 py-1 rounded-full text-sm"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Public Contact Information */}
            {((hostData.phoneNumberPublic && hostData.phoneNumber) || (hostData.emailPublic && hostData.email)) && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Information
                </label>
                <div className="bg-gray-700 rounded px-3 py-2 space-y-2">
                  {hostData.phoneNumberPublic && hostData.phoneNumber && (
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">📞</span>
                      <span className="text-gray-300">{hostData.phoneNumber}</span>
                    </div>
                  )}
                  {hostData.emailPublic && hostData.email && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-400">✉️</span>
                      <span className="text-gray-300">{hostData.email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Social Media Links */}
            {hostData.socialMedia && Object.values(hostData.socialMedia).some(value => value.trim()) && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Social Media
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(hostData.socialMedia).map(([platform, value]) => {
                    if (!value.trim()) return null;
                    
                    const platformIcons = {
                      instagram: '📷',
                      facebook: '📘',
                      tiktok: '🎵',
                      twitter: '🐦',
                      spotify: '🎧',
                      pandora: '🎼'
                    };
                    
                    const platformNames = {
                      instagram: 'Instagram',
                      facebook: 'Facebook',
                      tiktok: 'TikTok',
                      twitter: 'Twitter/X',
                      spotify: 'Spotify',
                      pandora: 'Pandora'
                    };
                    
                    return (
                      <a
                        key={platform}
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-gray-600 hover:bg-gray-500 rounded px-3 py-2 transition-colors duration-200 group"
                      >
                        <span className="text-xl">{platformIcons[platform]}</span>
                        <span className="text-sm text-gray-300 group-hover:text-white">{platformNames[platform]}</span>
                        <span className="text-xs text-green-400 group-hover:text-green-300">Visit Profile</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-6 text-center">
          <h3 className="text-xl font-bold text-white mb-4">
            Interested in booking this host?
          </h3>
          <p className="text-green-100 mb-6">
            Sign up to contact hosts, book games, and join the trivia community!
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate('/signup')}
              className="bg-white text-green-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Join as Host
            </button>
            <button
              onClick={() => navigate('/player-hub')}
              className="bg-gray-800 text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
            >
              View More Games
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicHostProfile; 